"use client";
import { Gallery } from "@andrew-wolf/react-photoswipe-gallery";
import dayjs from "dayjs";
import { useTranslations } from "next-intl";
import "photoswipe/dist/photoswipe.css";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "next/navigation";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { allTimezones } from "react-timezone-select";
import { useLocalStorage } from "utils";
import { useMediaContext, useReservationContext } from "../../context";
import { resolveTimeZone, wallClockInTimeZone } from "../../helpers/timeZone";
import {
  deleteMedia,
  printMedia,
  setPhotoSeen,
  setVideoSeen,
  transferMedia,
  updateMediaProperties,
  type UpdateMediaProperty,
} from "../../services";
import { Confirm } from "../confirm";
import { Loader } from "../loader";
import { toastMessage } from "../toast";
import DownloadDialog from "./DownloadDialog";
import {
  downloadMediaItems,
  type DownloadMediaItemsProgress,
  handleDownload,
  handleDownloadAsZip,
} from "./DownloadMediaItems";
import { MediaEnum } from "./enums";
import { isVideoItem } from "./helpers";
import {
  DisplayItem,
  FirstPageButton,
  LastPageButton,
  NextPageButton,
  PrevPageButton,
} from "./GalleryUIElements";
import MediaFilter from "./MediaFilter";
import MediaInfo from "./MediaInfo";
import SelectableWrapper from "./MediaWrappers/SelectableWrapper";
import PictureFilter from "./PictureFilter";
import ProgressBar from "./ProgressBar";
import SlideViewMode from "./SlideViewMode";
import type {
  DownloadOptionsType,
  FilterMediaByType,
  PhotoItemType,
  VideoItemType,
} from "./types";
import {
  filterItems,
  getPhotoSwipeCustomButtons,
  isItemUnProcessed,
} from "./utils";
import { ZipDownloader, ZipDownloaderProps } from "./ZipDownloader";

interface IMediaGalleryProps {
  data: (VideoItemType | PhotoItemType)[];
  showFilter?: boolean;
  reservationId?: string | null;
  photoSessionId?: string | null;
  showDelete?: boolean;
  showItemLength?: boolean;
  downloadOptions?: DownloadOptionsType;
  guestToken?: string | null;
  forceUpdate?: boolean;
  setForceUpdate?: React.Dispatch<React.SetStateAction<boolean>>;
  canDownloadAsZip?: boolean;
  printerAvailable?: boolean;
  cost?: number | null; // AI cost in cents
  budgetPercent?: number | "unlimited" | null; // AI budget remaining % – visible to everyone
  magicServices?: string | null;
  showPremiumServices?: boolean;
  onPremiumServicesClick?: () => void;
  /** The reservation's time zone; media timestamps are shown in it. */
  timeZone?: string | null;
}

export function MediaGallery({
  data = [],
  showFilter = true,
  reservationId = null,
  photoSessionId = null,
  showDelete = false,
  showItemLength = true,
  downloadOptions = { zip: true, separated: true },
  guestToken,
  forceUpdate,
  setForceUpdate,
  canDownloadAsZip,
  printerAvailable = false,
  cost,
  budgetPercent,
  magicServices,
  showPremiumServices,
  onPremiumServicesClick,
  timeZone: reservationTimeZone,
}: IMediaGalleryProps) {
  const timeZone = useMemo(
    () => resolveTimeZone(reservationTimeZone, Object.keys(allTimezones)),
    [reservationTimeZone]
  );
  const [filterBy, setFilterBy] = useLocalStorage<FilterMediaByType>(
    "galleryFilter",
    { video: true, photo: true }
  );
  const [showPrivacyFilter, setShowPrivacyFilter] = useLocalStorage<boolean>(
    "picturePrivacyFilterEnabled",
    false
  );
  const [privacyFilterItems, setPrivacyFilterItems] = useState<boolean[]>([
    false,
    false,
    false,
    false,
  ]);
  const [activeBackgroundFilterItems, setActiveBackgroundFilterItems] =
    useState<string[]>([]);
    const [items, setItems] = useState<(VideoItemType | PhotoItemType)[]>(data);
  const [isSlideViewActive, setIsSlideViewActive] = useState<boolean>(false);
  const [slideViewImageCount, setSlideViewImageCount] = useLocalStorage<number>(
    "slideViewImageCount",
    4
  );

  // Apply ? f= URL parameter to override localStorage filter
  const searchParams = useSearchParams();
  const galleryFilterStatus = searchParams.get("f");

  useEffect(() => {
    if (galleryFilterStatus !== null) {
      const status = Number(galleryFilterStatus);
      if (status === 1) {
        // f=1: photos only, video off
        setFilterBy({ video: false, photo: true });
      } else if (status === 2) {
        // f=2: photos and videos, both on
        setFilterBy({ video: true, photo: true });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [recentlySeenItems, setRecentlySeenItems] = useState<string[]>([]); // store in state
  const [isDeleteConfirmVisible, setIsDeleteConfirmVisible] =
    useState<boolean>(false);
  const [isSetMediaDateConfirmVisible, setIsSetMediaDateConfirmVisible] =
    useState<boolean>(false);
  const [newMediaDateValue, setNewMediaDateValue] = useState<Date | null>(
    new Date(Date.now())
  );
  const [isMoveConfirmVisible, setIsMoveConfirmVisible] =
    useState<boolean>(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState<
    DownloadMediaItemsProgress | undefined
  >(undefined);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [loadingText, setLoadingText] = useState<string>("");
  const [downloadableItems, setDownloadableItems] = useState<
    (PhotoItemType | VideoItemType)[]
  >([]);
  const [zipDownloads, setZipDownloads] = useState<ZipDownloaderProps[]>([]);
  const [unProcessedIds, setUnProcessedIds] = useState<string[]>([]); // all ids
  const [unProcessedIdsInCurrentPeriod, setUnProcessedIdsInCurrentPeriod] =
    useState<string[]>([]); // ids only in period
  const { selectedItems, clearSelectedMedia, pageSizeState } =
    useMediaContext();
  const downloadDialogRef = useRef<HTMLDialogElement | null>(null);
  const filteredItems = useMemo(
    () =>
      filterItems(
        items,
        filterBy,
        showFilter,
        activeBackgroundFilterItems,
        showPrivacyFilter,
        privacyFilterItems
      ),
    [
      showFilter,
      items,
      filterBy,
      activeBackgroundFilterItems,
      showPrivacyFilter,
      privacyFilterItems,
    ]
  );
  const [paginatedItemsState, setPaginatedItemsState] = useState<
    (VideoItemType | PhotoItemType)[]
  >([]);
  const t = useTranslations();
  const { reservationId: selectedReservationId } = useReservationContext();

  const getSelectedIds = () => {
    type ReduceReturnType = { photoIds: string[]; videoIds: string[] };

    const { photoIds, videoIds } = Array.from(
      new Set(selectedItems)
    ).reduce<ReduceReturnType>(
      (acc, id: string) => {
        const item = filteredItems.find((e) => e.id === id);

        if (item?.mediaType === MediaEnum.Photo) {
          acc.photoIds.push(id);
        } else if (item?.mediaType === MediaEnum.Video) {
          // AI generated videos (VEO) are stored in Photo table, not VideoFile table.
          // They come from the photo list API and have photoSessionId property.
          // These must be sent as photoIds so the backend deletes from Photo table.
          if ('photoSessionId' in item) {
            acc.photoIds.push(id);
          } else {
            acc.videoIds.push(id);
          }
        }

        return acc;
      },
      { photoIds: [], videoIds: [] }
    );

    return { photoIds, videoIds };
  };

  const handleDelete = async () => {
    setIsLoading(true);
    setLoadingText(t("Gallery.deleting"));

    const { photoIds, videoIds } = getSelectedIds();

    setIsDeleteConfirmVisible(false);
    clearSelectedMedia();

    let res = null;

    try {
      if (photoSessionId) {
        if (guestToken) {
          res = await deleteMedia(photoIds, videoIds, guestToken);
        } else {
          setIsLoading(false);
          toastMessage.error("No token found", 15000);
        }
      } else {
        res = await deleteMedia(photoIds, videoIds);
      }

      if (res && res.status === 200) {
        setItems(items.filter((item) => !selectedItems.includes(item.id)));
        setIsLoading(false);
        setLoadingText("");
        toastMessage.success(t("Gallery.deleteSuccess"));
      }
    } catch (error: any) {
      toastMessage.error(
        `Error ${error.response.status}: ${error.response.statusText}`
      );
      setIsLoading(false);
      setLoadingText("");
    }
  };

  const tryToInitMove = () => {
    if (selectedReservationId === "") {
      toastMessage.error(t("Gallery.moveErrorNoSelectedReservation"));
      return;
    }
    if (selectedReservationId == reservationId) {
      toastMessage.error(t("Gallery.moveErrorSameReservation"));
      return;
    }

    setIsMoveConfirmVisible(true);
  };

  const handlePrint = async () => {
    const { photoIds } = getSelectedIds();
    if (photoIds.length === 0) {
      toastMessage.error(t("Gallery.printNoPhotos"));
      return;
    }
    setIsLoading(true);
    setLoadingText(t("Gallery.printing"));
    try {
      const res = guestToken
        ? await printMedia(photoIds, guestToken)
        : await printMedia(photoIds);
      if (res?.success) {
        toastMessage.success(t("Gallery.printSent"));
        clearSelectedMedia();
      } else {
        toastMessage.error(res?.message || t("Gallery.printFailed"));
      }
    } catch {
      toastMessage.error(t("Gallery.printFailed"));
    } finally {
      setIsLoading(false);
    }
  };

  const handleMove = async () => {
    setIsLoading(true);
    setLoadingText(t("Gallery.moving"));
    try {
      await transferMedia(selectedReservationId, selectedItems);
      setIsMoveConfirmVisible(false);
      setItems(items.filter((item) => !selectedItems.includes(item.id)));
      setIsLoading(false);
      setLoadingText("");
      clearSelectedMedia();
    } catch (error: any) {
      toastMessage.error(
        `Error ${error.response.status}: ${error.response.statusText}`
      );
      setIsLoading(false);
      setLoadingText("");
    }
  };

  const handlePhotoVideoSeen = async (item: VideoItemType | PhotoItemType) => {
    if (!item.seenOnUtc) {
      try {
        if (item.mediaType === MediaEnum.Photo) {
          await setPhotoSeen(item.id);
        } else {
          await setVideoSeen(item.id);
        }

        setRecentlySeenItems((ids) => {
          if (!ids.includes(item.id)) {
            return [...ids, item.id];
          }

          return [...ids];
        });
      } catch (error: any) {
        // axios error
        console.error(error.message);
      }
    }
  };

  const handleMediaDateChange = async () => {
    setIsSetMediaDateConfirmVisible(false);

    type MediaWithTime = { id: string; time: dayjs.Dayjs };

    let itemsToUpdate = selectedItems.reduce<MediaWithTime[]>(
      (acc, id: string) => {
        const item = filteredItems.find((e) => e.id === id);

        if (item?.mediaType === MediaEnum.Photo) {
          acc.push({ id: id, time: dayjs(item.takenOnUtc) });
        } else if (isVideoItem(item)) {
          acc.push({ id: id, time: dayjs(item.recordDate) });
        }

        return acc;
      },
      []
    );

    itemsToUpdate = itemsToUpdate.sort((a, b) => a.time.diff(b.time));

    // The picked time is meant in the reservation's time zone, like the
    // timestamps shown on the gallery items.
    const newMediaDate = wallClockInTimeZone(newMediaDateValue, timeZone);
    for (let i = 1; i < itemsToUpdate.length; i++) {
      itemsToUpdate[i].time = newMediaDate.add(
        itemsToUpdate[i].time.diff(itemsToUpdate[0].time)
      );
    }
    itemsToUpdate[0].time = newMediaDate;

    try {
      const updates = itemsToUpdate.map<UpdateMediaProperty>((x) => {
        return { id: x.id, takenOnUtc: x.time.toISOString() };
      });
      await updateMediaProperties(updates);
      toastMessage.success(t("Toast.successSetNewDateForGalleryItems"));
    } catch (error: any) {
      console.error(error.message);
      toastMessage.error(t("Toast.errorSetNewDateForGalleryItems"));
    }
    setForceUpdate && setForceUpdate(!forceUpdate);
  };

  const onBeforeOpen = (pswpInstance: any) => {
    // This code handles the back button on mobile (back button closes the modal)
    if (
      !history.state ||
      !history.state.pswp ||
      history.state.path !== window.location.pathname
    ) {
      history.pushState(
        { pswp: true, path: window.location.pathname },
        "",
        window.location.pathname + window.location.search
      );
    }
    window.addEventListener("popstate", () => {
      if (
        typeof pswpInstance === "object" &&
        pswpInstance &&
        pswpInstance.isOpen
      ) {
        history.pushState(
          { pswp: true, path: window.location.pathname },
          "",
          window.location.pathname + window.location.search
        );
        pswpInstance.close();
      } else {
        history.go(-1);
      }
    });

    pswpInstance.options.padding = { top: 0, bottom: 0, left: 0, right: 0 };
    pswpInstance.on("change", () => {
      const index = pswpInstance.currIndex;
      const item = filteredItems[index];

      if (item.mediaType === MediaEnum.Photo) {
        handlePhotoVideoSeen(item);
      }
    });
  };

  const options = {
    bgOpacity: 0.95,
    close: false,
    padding: { top: 0, bottom: 0, left: 0, right: 300 },
  };
  const uiElements = useMemo(() => {
    return getPhotoSwipeCustomButtons(paginatedItemsState);
  }, [paginatedItemsState]);

  function onDownloadProgressChanged(progress: DownloadMediaItemsProgress) {
    //console.log(`Progress changed: ${progress.progress}`);
    setDownloadProgress(progress);
    if (progress.starting) {
      setIsDownloading(true);
      toastMessage.success(t("Gallery.downloadStarted"));
    } else if (progress.completed) {
      setIsDownloading(false);
      if (progress.failedCount) {
        toastMessage.error(
          `${t("Gallery.downloadCompleted")} (${progress.failedCount})`
        );
      } else {
        toastMessage.success(t("Gallery.downloadCompleted"));
      }
    }
  }

  function onZipBuild(props: ZipDownloaderProps) {
    setZipDownloads([...zipDownloads, props]);
  }

  useEffect(() => {
    setItems(data);

    const unProcessedItems = data.filter((item) => isItemUnProcessed(item));
    const ids = unProcessedItems.map((e) => e.id);

    setUnProcessedIds(ids);

    if (ids.length === 0) {
      setUnProcessedIdsInCurrentPeriod([]);
    } else {
      setUnProcessedIdsInCurrentPeriod((prev) => {
        const newItems: string[] = [];

        ids.forEach((unProcessedId) => {
          if (!prev.includes(unProcessedId)) {
            newItems.push(unProcessedId);
          }
        });

        return [...prev, ...newItems];
      });
    }
  }, [data]);

  return (
    <div className="mb-6">
      {filteredItems.length > 0 ? (
        <ProgressBar
          items={items}
          unProcessedIdsInCurrentPeriod={unProcessedIdsInCurrentPeriod}
          unProcessedIds={unProcessedIds}
          key={unProcessedIdsInCurrentPeriod.join("_")}
        />
      ) : null}

      {isDownloading && (
        <div className="flex flex-row gap-5 items-center">
          {`Downloading ${downloadProgress?.fileName}:`}
          <progress
            className="progress progress-primary w-56"
            value={downloadProgress?.progress ?? 0}
            max="100"
          ></progress>
        </div>
      )}

      {zipDownloads.map((z) => (
        <ZipDownloader key={z.fileName} {...z} />
      ))}

      <MediaInfo items={items} showFilter={showFilter} cost={cost} budgetPercent={budgetPercent} magicServices={magicServices} />

      {showFilter || isLoading || showItemLength ? (
        <div className="relative flex flex-col md:flex-row items-left md:items-center">
          {showFilter ? (
            <MediaFilter
              items={items}
              filterBy={filterBy}
              setFilterBy={setFilterBy}
              showFilter={showFilter}
              isSlideViewActive={isSlideViewActive}
              setIsSlideViewActive={setIsSlideViewActive}
              slideViewImageCount={slideViewImageCount}
              setSlideViewImageCount={setSlideViewImageCount}
            />
          ) : null}

          {isLoading ? (
            <div className="w-10 flex items-center justify-center absolute left-[50%] top-4">
              <div className="w-7 h-7 ">
                <Loader />
              </div>
              <span className="ml-2">{loadingText}</span>
            </div>
          ) : null}

          {showItemLength ? (
            <div className="ml-auto text-sm">
              {t("Gallery.showItems", { qty: filteredItems.length })}
            </div>
          ) : null}
        </div>
      ) : null}

      {showFilter || isLoading || showItemLength ? (
        <div className="relative flex flex-col md:flex-row items-left md:items-center my-1">
          <PictureFilter
            items={items}
            filterBy={filterBy}
            setFilterBy={setFilterBy}
            showPrivacyFilter={showPrivacyFilter}
            setShowPrivacyFilter={setShowPrivacyFilter}
            privacyFilterItems={privacyFilterItems}
            setPrivacyFilterItems={setPrivacyFilterItems}
            activeBackgroundFilterItems={activeBackgroundFilterItems}
            setActiveBackgroundFilterItems={setActiveBackgroundFilterItems}
            showFilter={showFilter}
          />
        </div>
      ) : null}

      <SelectableWrapper
        items={filteredItems.map((filteredItem) => filteredItem.id)}
        showDelete={showDelete}
        handleDelete={() => setIsDeleteConfirmVisible(true)}
        handleSetMediaDate={() => setIsSetMediaDateConfirmVisible(true)}
        handleDownload={(downloadAll: boolean) =>
          handleDownload(
            filteredItems,
            selectedItems,
            setDownloadableItems,
            downloadOptions,
            downloadDialogRef,
            photoSessionId,
            onDownloadProgressChanged,
            onZipBuild,
            reservationId,
            setIsLoading,
            downloadAll,
            guestToken,
            timeZone
          )
        }
        handleMove={tryToInitMove}
        handlePrint={handlePrint}
        printerAvailable={printerAvailable}
        showPremiumServices={showPremiumServices}
        onPremiumServicesClick={onPremiumServicesClick}
      >
        <Gallery
          options={options}
          onBeforeOpen={onBeforeOpen}
          uiElements={uiElements}
          pagination={{
            items: filteredItems,
            pageSize: pageSizeState,
            paginationControl: "top-bottom-button",
            UIElements: {
              FirstPageButton,
              PrevPageButton,
              NextPageButton,
              LastPageButton,
            },
            displayItem: (
              paginatedItems: any[]
              // pageNumber: number,
              // pageSize: number,
            ) =>
              DisplayItem(
                paginatedItems,
                setPaginatedItemsState,
                handlePhotoVideoSeen,
                recentlySeenItems,
                reservationId,
                timeZone
              ),
          }}
          // ProcessingUIElement={ProcessingUIElement}
        />
      </SelectableWrapper>

      <Confirm
        handleOk={handleDelete}
        handleClose={() => setIsDeleteConfirmVisible(false)}
        isOpened={isDeleteConfirmVisible}
        setIsOpened={(isOpen) => setIsDeleteConfirmVisible(isOpen)}
        okBtnText={t("Buttons.delete")}
        title={t("Gallery.deleteConfirmTitle")}
        t={t}
      />

      <Confirm
        handleOk={() => {
          handleMediaDateChange();
        }}
        handleClose={() => {
          setIsSetMediaDateConfirmVisible(false);
        }}
        isOpened={isSetMediaDateConfirmVisible}
        setIsOpened={(isOpen) => setIsSetMediaDateConfirmVisible(isOpen)}
        okBtnText={t("Gallery.setMediaDate")}
        title={t("Gallery.setMediaDateConfirmTitle")}
        t={t}
      >
        <div className="h-[320px]">
          <DatePicker
            wrapperClassName="w-full"
            showTimeSelect
            selected={newMediaDateValue}
            onChange={(date) => {
              setNewMediaDateValue(date);
            }}
            isClearable
            timeFormat="HH:mm"
            timeIntervals={1}
            dateFormat="yyyy-MM-dd HH:mm"
            calendarStartDay={1}
            className="block h-[48px] w-full px-4 py-2 border rounded-md shadow-sm focus:outline-none focus:border-black focus:border-2"
          />
        </div>
      </Confirm>

      <Confirm
        handleOk={handleMove}
        handleClose={() => setIsMoveConfirmVisible(false)}
        isOpened={isMoveConfirmVisible}
        setIsOpened={(isOpen) => setIsMoveConfirmVisible(isOpen)}
        okBtnText={t("Gallery.moveButton")}
        title={t("Gallery.moveConfirmTitle")}
        t={t}
      />

      <DownloadDialog
        canDownloadAsZip={canDownloadAsZip}
        dialogRef={downloadDialogRef}
        downloadableItems={downloadableItems}
        handleDownloadSeparately={(itemsToDownload: any, callback: any) => {
          callback();
          downloadMediaItems(
            itemsToDownload,
            onDownloadProgressChanged,
            timeZone
          );
        }}
        handleDownloadAsZip={(itemsToDownload: any, callback: any) =>
          handleDownloadAsZip(
            reservationId,
            photoSessionId,
            setIsLoading,
            itemsToDownload,
            onZipBuild,
            callback,
            guestToken
          )
        }
      />

      {isSlideViewActive && (
        <SlideViewMode
          items={filteredItems}
          imageCount={slideViewImageCount}
          onClose={() => setIsSlideViewActive(false)}
          showVideos={filterBy.video}
        />
      )}
    </div>
  );
}
